
import React, { useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ProfileHeader } from "@/components/profile/sections/ProfileHeader";
import { ProfileBio } from "@/components/profile/sections/ProfileBio";
import { ProfileLocation } from "@/components/profile/sections/ProfileLocation";
import { ProfileLanguages } from "@/components/profile/sections/ProfileLanguages";
import { HostSpecificInfo } from "@/components/profile/sections/HostSpecificInfo";
import { BecomeHostSection } from "@/components/profile/sections/BecomeHostSection";
import { ProfileSkeleton } from "@/components/profile/sections/ProfileSkeleton";
import { MediaSection } from "@/components/profile/sections/MediaSection";
import { BlogSection } from "@/components/host/blog/BlogSection";
import { VerificationSection } from "@/components/profile/sections/VerificationSection";
import { ProfileTabs } from "@/components/profile/sections/ProfileTabs";
import { SEO } from "@/components/SEO";
import { MapPin, Users, Calendar, Clock, Bookmark, FolderHeart, Star, Lock } from 'lucide-react';
import { SecuritySettings } from "@/components/profile/sections/SecuritySettings";

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  const hash = location.hash.replace('#', '');
  const defaultTab = hash || 'profile';
  const profileId = params.id;

  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
  });

  const { data: profile, isLoading: isProfileLoading, refetch } = useQuery({
    queryKey: ['profile', profileId || session?.user?.id],
    enabled: !!session || !!profileId,
    queryFn: async () => {
      const targetId = profileId || session?.user?.id;
      
      if (!targetId) throw new Error("No profile ID available");
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          is_verified,
          verification_date,
          google_maps_rating,
          google_maps_reviews_count,
          trustpilot_rating,
          trustpilot_reviews_count
        `)
        .eq('id', targetId)
        .single();
      
      if (error) throw error;
      
      if (data && data.username && !profileId && data.id !== session?.user?.id) {
        navigate(`/profiles/${data.username}/${data.id}`, { replace: true });
      }
      
      return data;
    },
  });

  useEffect(() => {
    if (!isSessionLoading && !session && !profileId) {
      navigate('/auth');
    }
  }, [session, isSessionLoading, navigate, profileId]);

  const handleRefetch = async () => {
    await refetch();
  };

  if (isSessionLoading || isProfileLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ProfileSkeleton />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <SEO 
          title="Profile Not Found"
          description="Please log in to view your profile on Travilis."
          noIndex={true}
        />
        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <h2 className="mt-4 text-xl font-semibold">Profile Not Found</h2>
              <p className="mt-2 text-gray-600">
                {!session ? "Please log in to view your profile." : "The requested profile could not be found."}
              </p>
              <Button 
                onClick={() => navigate(session ? '/' : '/auth')}
                className="mt-4"
              >
                {session ? "Go Home" : "Log In"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getProfileDescription = () => {
    if (profile.bio) {
      return profile.bio.length > 160 ? `${profile.bio.substring(0, 157)}...` : profile.bio;
    }
    return `${profile.username}'s profile on Travilis. ${profile.role === 'host' ? 'Connect with this local host.' : 'Join them on travel adventures.'}`;
  };

  const isOwnProfile = session?.user?.id === profile.id;

  const profileContent = (
    <div className="space-y-6">
      {profile.role === 'host' && (
        <VerificationSection 
          isVerified={profile.is_verified || false}
          verificationDate={profile.verification_date}
          hostId={profile.id}
          sessionUserId={session?.user?.id || ''}
          googleMapsRating={profile.google_maps_rating}
          googleMapsReviewsCount={profile.google_maps_reviews_count}
          trustpilotRating={profile.trustpilot_rating}
          trustpilotReviewsCount={profile.trustpilot_reviews_count}
        />
      )}
      
      <ProfileBio bio={profile.bio} />
      <ProfileLocation location={profile.location} />
      <ProfileLanguages languages={profile.languages || []} />

      {profile.role === 'host' ? (
        <HostSpecificInfo
          hostingStyle={profile.hosting_style}
          specialties={profile.specialties || []}
        />
      ) : (
        isOwnProfile && <BecomeHostSection userId={profile.id} />
      )}
    </div>
  );

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      content: profileContent,
    },
    {
      id: 'blog',
      label: 'Blog',
      content: (
        <BlogSection
          hostId={profile.id}
          isOwnProfile={isOwnProfile}
        />
      ),
      condition: profile.role === 'host'
    },
    {
      id: 'media',
      label: 'Media',
      content: (
        <MediaSection 
          userId={profile.id} 
          isOwnProfile={isOwnProfile} 
        />
      )
    },
    {
      id: 'security',
      label: 'Security',
      content: isOwnProfile ? <SecuritySettings /> : null,
      condition: isOwnProfile,
      icon: <Lock className="w-4 h-4" />
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title={`${profile.username}'s Profile`}
        description={getProfileDescription()}
        image={profile.avatar_url || '/og-image.png'}
        canonical={`/profiles/${profile.username}/${profile.id}`}
      />
      
      <Card className="max-w-3xl mx-auto overflow-hidden">
        <CardContent className="p-0">
          <div className="mb-6">
            <ProfileHeader 
              profile={profile} 
              session={session}
              refetch={handleRefetch}
            />
          </div>
          
          <div className="p-6">
            <ProfileTabs 
              tabs={tabs} 
              defaultTab={defaultTab} 
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
